import React, {
  createContext,
  useMemo,
  useReducer,
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react'
import useSWR from 'swr'
import { useMerchant } from './MerchantContext'
import { fetcher, staticOptions } from '../core/api-client'
import { apiURI } from '../utils/constants'
import { DynamicSegmentDimensionType, DynamicSegmentFilterType, DynamicSegmentFilterErrorType, DynamicSegmentFormErrorStateType, DynamicSegmentFormStateType, DynamicSegmentRelationDataType, DynamicSegmentSchemaDataType, SegmentConstructType } from '../types/segment'
import { defaultDynamicSegmentFormErrorState, defaultDynamicSegmentFormState, defaultRelationData, defaultSchemaData } from 'src/assets/SegmentsConfig'
import produce from 'immer'
import {
  createSegment,
  createDynamicSegmentPayload,
  editSegment,
  getSegmentById,
  getTemplateById,
  isDynamicSegmentFilterValueSlotEmpty,
  parseSegmentResponseToFormState
} from '../utils/segmentsHelper'
import { errorToast, successToast } from '../utils/toasters'
import { useRouter } from 'next/router'
import isEqual from 'lodash/isEqual'

export enum DynamicSegmentActionSetType {
  SET_SEGMENT_FORM_STATE,
  ADD_NEW_FILTER,
  DELETE_FILTER,
  UPDATE_FILTER,
  SET_SEGMENT_NAME,
  SET_SEGMENT_DESCRIPTION,
  SET_SEGMENT_TAGS,
  SET_PREVIEW_FILTERS,
}

export type DynamicSegmentActionType =
  | { type: DynamicSegmentActionSetType.SET_SEGMENT_FORM_STATE; payload: DynamicSegmentFormStateType }
  | { type: DynamicSegmentActionSetType.ADD_NEW_FILTER; payload: DynamicSegmentFilterType }
  | { type: DynamicSegmentActionSetType.DELETE_FILTER; payload: string }
  | { type: DynamicSegmentActionSetType.UPDATE_FILTER; payload: DynamicSegmentFilterType }
  | { type: DynamicSegmentActionSetType.SET_SEGMENT_NAME; payload: string }
  | { type: DynamicSegmentActionSetType.SET_SEGMENT_DESCRIPTION; payload: string }
  | { type: DynamicSegmentActionSetType.SET_SEGMENT_TAGS; payload: string[] }
  | { type: DynamicSegmentActionSetType.SET_PREVIEW_FILTERS; payload: DynamicSegmentDimensionType[] }

interface DynamicSegmentContextType {
  segmentFormState: DynamicSegmentFormStateType
  relationData: DynamicSegmentRelationDataType
  schemaData: DynamicSegmentSchemaDataType
  formErrorState: DynamicSegmentFormErrorStateType
  nameErrorKey: number
  hasPreviewChanges: boolean
  isLoading: boolean
  /** Segment/template JSON fetch — use for right-panel skeleton without blocking the whole flow */
  isLoadingSegment: boolean
  /** Dynamic segment save request in flight — use for primary button filling loader */
  isSavingSegment: boolean
  isLoadingSchemaData: boolean
  isEditMode: boolean
  /** `/segments/view` — opened from manager; discard returns to /segments */
  isViewMode: boolean
  actions: {
    setSegmentFormState: (state: DynamicSegmentFormStateType) => void
    setFormErrorState: (errorState: DynamicSegmentFormErrorStateType) => void
    addNewFilter: (filter: DynamicSegmentFilterType) => void
    deleteFilter: (filterId: string) => void
    updateFilter: (filter: DynamicSegmentFilterType) => void
    setSegmentName: (name: string) => void
    setSegmentDescription: (description: string) => void
    setSegmentTags: (tags: string[]) => void
    setPreviewFilters: (filters: DynamicSegmentDimensionType[]) => void
    resetPreviewChanges: () => void
    validateForm: (opts?: { skipName?: boolean }) => number
    saveSegment: () => Promise<number | void>
    /** Load a predefined segment template into the form (same payload as /create?templateId=) */
    applyTemplateById: (templateId: string) => Promise<boolean>
    /** Invalidate cached segment fetch so the next effect run reloads from API (e.g. discard changes on /segments/view) */
    reloadSegmentFromApi: () => void
    /** Discard without saving: close slide-in if `onDiscard` set, else go to /segments on /view, else router.back */
    discardWorkspace: () => void
    /** Host finished applying initial filters (e.g. Sankey prefill) — hide definition-panel skeleton */
    completeHydration: () => void
  }
}

const DynamicSegmentContext = createContext<DynamicSegmentContextType | undefined>(undefined)

const reducer = produce((draft: DynamicSegmentFormStateType, action: DynamicSegmentActionType) => {
  switch (action.type) {
    case DynamicSegmentActionSetType.SET_SEGMENT_FORM_STATE:
      return action.payload
    case DynamicSegmentActionSetType.ADD_NEW_FILTER:
      draft.filters.push(action.payload)
      break
    case DynamicSegmentActionSetType.DELETE_FILTER:
      draft.filters = draft.filters.filter(f => f.id !== action.payload)
      break
    case DynamicSegmentActionSetType.UPDATE_FILTER:
      const filterIndex = draft.filters.findIndex(f => f.id === action.payload.id)
      if (filterIndex !== -1) {
        draft.filters[filterIndex] = action.payload
      }
      break
    case DynamicSegmentActionSetType.SET_SEGMENT_NAME:
      draft.name = action.payload
      break
    case DynamicSegmentActionSetType.SET_SEGMENT_DESCRIPTION:
      draft.description = action.payload
      break
    case DynamicSegmentActionSetType.SET_SEGMENT_TAGS:
      draft.tags = action.payload
      break
    case DynamicSegmentActionSetType.SET_PREVIEW_FILTERS:
      draft.previewFilters = action.payload
      break
    default:
      break
  }
})



interface DynamicSegmentProviderProps {
  children: React.ReactNode
  onSuccess?: () => void
  /** e.g. segments manager slide-in: close sheet without saving */
  onDiscard?: () => void
  /** Apply template when schema is ready (e.g. segments dialog: pick template → dynamic view) */
  templateIdToApply?: string | null
  onTemplateApplied?: () => void
  /** Show definition-panel skeleton until the host calls `completeHydration` (e.g. Sankey prefill). */
  hydrateOnMount?: boolean
}

const DynamicSegmentProvider = ({
  children,
  onSuccess,
  onDiscard,
  templateIdToApply = null,
  onTemplateApplied,
  hydrateOnMount = false
}: DynamicSegmentProviderProps) => {
  const {
    getters: { merchant }
  } = useMerchant()
  const router = useRouter()

  const [segmentFormState, dispatch] = useReducer(reducer, defaultDynamicSegmentFormState)
  const [formErrorState, setFormErrorState] = useState<DynamicSegmentFormErrorStateType>(defaultDynamicSegmentFormErrorState)
  const [nameErrorKey, setNameErrorKey] = useState(0)
  const [isLoadingSegment, setIsLoadingSegment] = useState<boolean>(hydrateOnMount)
  const [isSavingSegment, setIsSavingSegment] = useState<boolean>(false)
  const [segmentReloadNonce, setSegmentReloadNonce] = useState(0)

  // Temporary state to track preview-related changes
  const [tempPreviewFilters, setTempPreviewFilters] = useState<DynamicSegmentDimensionType[]>([])
  const [tempFilters, setTempFilters] = useState<DynamicSegmentFilterType[]>(
    defaultDynamicSegmentFormState.filters
  )
  const [tempName, setTempName] = useState('')
  const [tempDescription, setTempDescription] = useState('')
  const [tempTags, setTempTags] = useState<string[]>([])

  const hasPreviewChanges = useMemo(() => {
    return (
      !isEqual(segmentFormState.previewFilters, tempPreviewFilters) ||
      !isEqual(segmentFormState.filters, tempFilters) ||
      segmentFormState.name !== tempName ||
      (segmentFormState.description ?? '') !== tempDescription ||
      !isEqual(segmentFormState.tags ?? [], tempTags)
    )
  }, [
    segmentFormState.previewFilters,
    segmentFormState.filters,
    segmentFormState.name,
    segmentFormState.description,
    segmentFormState.tags,
    tempPreviewFilters,
    tempFilters,
    tempName,
    tempDescription,
    tempTags
  ])

  const { segment_flow, segmentId, templateId } = router?.query

  const segmentIdStr = typeof segmentId === 'string' ? segmentId : undefined
  const isViewMode = segment_flow?.[0] === 'view' && Boolean(segmentIdStr)
  const isEditRoute = segment_flow?.[0] === 'edit' && Boolean(segmentIdStr)
  /** Existing segment from manager: `/segments/view` or `/segments/edit` */
  const isEditMode = isViewMode || isEditRoute
  const isCreateFromTemplate = segment_flow?.[0] === 'create' && typeof templateId === 'string' && Boolean(templateId)

  const merchantAccountId = merchant?.accountId || ''

  // Fetch relation data from API
  const {
    data: relationDataResponse,
    error: relationDataError,
    isValidating: isLoadingRelationData
  } = useSWR(
    merchantAccountId
      ? [`/merchant/${merchantAccountId}/segments/sortment/relation`, apiURI.SEGMENTS_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const relationData: DynamicSegmentRelationDataType = useMemo(() => {
    if (isLoadingRelationData || relationDataError || !relationDataResponse) {
      return defaultRelationData
    }
    const responseData = relationDataResponse as any
    if (!responseData.data) {
      return defaultRelationData
    }
    return {
      relationId: responseData.data.relation_id,
      dimensions: responseData.data.dimensions.map((dim: any) => ({
        id: dim?.id,
        name: dim?.name,
        tableName: dim?.table_name,
        tableType: dim?.table_type
      }))
    }
  }, [relationDataResponse, isLoadingRelationData, relationDataError])

  // Fetch schema data from API
  const {
    data: schemaDataResponse,
    error: schemaDataError,
    isValidating: isLoadingSchemaData
  } = useSWR(
    merchantAccountId
      ? [`/merchant/${merchantAccountId}/segments/schema`, apiURI.SEGMENTS_ENGINE]
      : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const schemaData: DynamicSegmentSchemaDataType = useMemo(() => {
    if (isLoadingSchemaData || schemaDataError || !schemaDataResponse) {
      return defaultSchemaData
    }
    const responseData = schemaDataResponse as any
    if (!responseData.data) {
      return defaultSchemaData
    }
    const parsedSchemaData: DynamicSegmentSchemaDataType = {
      fields: responseData.data.fields.map((field: any) => ({
        id: field?.id,
        columnReference: field?.column_reference,
        columnType: field?.column_type,
        name: field?.name,
        category: field?.category,
        dataType: field?.data_type,
        parentTableId: field?.parent_table,
        tableId: field?.table_name,
        relationship: field?.relationship,
        operators: field?.operators?.map((operator: any) => ({
          key: operator?.key,
          label: operator?.label,
          dataType: operator?.datatype,
          valueCount: operator?.value_count
        })),
        description: field?.description,
        allowedValues: field?.allowed_values,
        metadata: field?.metadata || {}
      })),
      dataTypes: responseData.data?.data_types || []
    }
    return parsedSchemaData
  }, [schemaDataResponse, isLoadingSchemaData, schemaDataError])

  /** Latest schema for segment fetch — avoids re-running load effect when only useMemo identity changes. */
  const schemaDataRef = useRef(schemaData)
  schemaDataRef.current = schemaData

  /** Skip duplicate getSegmentById + skeleton when effect re-fires for the same segment (Strict Mode / SWR churn). */
  const segmentHydratedKeyRef = useRef<string | null>(null)

  // Action callbacks
  const setSegmentFormStateAction = useCallback((state: DynamicSegmentFormStateType) => {
    dispatch({
      type: DynamicSegmentActionSetType.SET_SEGMENT_FORM_STATE,
      payload: state
    })
  }, [])

  const addNewFilter = useCallback((filter: DynamicSegmentFilterType) => {
    dispatch({
      type: DynamicSegmentActionSetType.ADD_NEW_FILTER,
      payload: filter
    })
  }, [])

  const deleteFilter = useCallback((filterId: string) => {
    dispatch({
      type: DynamicSegmentActionSetType.DELETE_FILTER,
      payload: filterId
    })
  }, [])

  const updateFilter = useCallback((filter: DynamicSegmentFilterType) => {
    dispatch({
      type: DynamicSegmentActionSetType.UPDATE_FILTER,
      payload: filter
    })
  }, [])

  const setSegmentName = useCallback((name: string) => {
    dispatch({
      type: DynamicSegmentActionSetType.SET_SEGMENT_NAME,
      payload: name
    })
  }, [])

  const setSegmentDescription = useCallback((description: string) => {
    dispatch({
      type: DynamicSegmentActionSetType.SET_SEGMENT_DESCRIPTION,
      payload: description
    })
  }, [])

  const setSegmentTags = useCallback((tags: string[]) => {
    dispatch({
      type: DynamicSegmentActionSetType.SET_SEGMENT_TAGS,
      payload: tags
    })
  }, [])

  const setPreviewFilters = useCallback((filters: DynamicSegmentDimensionType[]) => {
    dispatch({
      type: DynamicSegmentActionSetType.SET_PREVIEW_FILTERS,
      payload: filters
    })
  }, [])

  const resetPreviewChanges = useCallback(() => {
    setTempPreviewFilters(segmentFormState.previewFilters)
    setTempFilters(segmentFormState.filters)
    setTempName(segmentFormState.name)
    setTempDescription(segmentFormState.description ?? '')
    setTempTags(segmentFormState.tags ? [...segmentFormState.tags] : [])
  }, [
    segmentFormState.previewFilters,
    segmentFormState.filters,
    segmentFormState.name,
    segmentFormState.description,
    segmentFormState.tags
  ])

  const fetchTemplateByIdAndApply = useCallback(
    async (id: string): Promise<'success' | 'not_ready' | 'failed'> => {
      if (!merchantAccountId) {
        return 'failed'
      }
      if (isLoadingRelationData || isLoadingSchemaData || schemaData.fields.length === 0) {
        return 'not_ready'
      }

      setIsLoadingSegment(true)
      try {
        const response: any = await getTemplateById(merchantAccountId, id)

        if (response?.error) {
          errorToast(response.error.message || 'Failed to load template')
          console.error('x-trace-id:', response.headers?.['x-trace-id'])
          return 'failed'
        }

        if (response?.data?.data) {
          const parsedFormState = parseSegmentResponseToFormState(response.data.data, schemaData)
          setSegmentFormStateAction(parsedFormState)
          setFormErrorState(defaultDynamicSegmentFormErrorState)
          setTempPreviewFilters(parsedFormState.previewFilters)
          setTempFilters(parsedFormState.filters)
          setTempName(parsedFormState.name)
          setTempDescription(parsedFormState.description ?? '')
          setTempTags(parsedFormState.tags ? [...parsedFormState.tags] : [])
          return 'success'
        }
        errorToast('Failed to load template')
        return 'failed'
      } catch (error) {
        console.error('Error fetching template:', error)
        errorToast('Failed to load template')
        return 'failed'
      } finally {
        setIsLoadingSegment(false)
      }
    },
    [merchantAccountId, isLoadingRelationData, isLoadingSchemaData, schemaData, setSegmentFormStateAction]
  )

  const applyTemplateById = useCallback(
    async (templateId: string): Promise<boolean> => {
      const result = await fetchTemplateByIdAndApply(templateId)
      if (result === 'not_ready') {
        errorToast('Schema is still loading. Try again in a moment.')
        return false
      }
      return result === 'success'
    },
    [fetchTemplateByIdAndApply]
  )

  // Validate filter recursively
  const validateFilter = (filter: DynamicSegmentFilterType): DynamicSegmentFilterErrorType => {
    const filterError: DynamicSegmentFilterErrorType = {
      id: filter.id,
      values: { status: false, message: '' },
      children: []
    }

    // Validate values based on operator valueCount
    const { valueCount, dataType } = filter.operator
    
    // Skip validation for BOOLEAN type (no values needed)
    if (dataType !== 'BOOLEAN') {
      // Check if required number of values are present
      if (valueCount === 1) {
        if (
          !filter.values ||
          filter.values.length < 1 ||
          isDynamicSegmentFilterValueSlotEmpty(filter.values[0])
        ) {
          filterError.values = {
            status: true,
            message: 'Please set the values properly.'
          }
        }
      } else if (valueCount === 2) {
        if (
          !filter.values ||
          filter.values.length < 2 ||
          isDynamicSegmentFilterValueSlotEmpty(filter.values[0]) ||
          isDynamicSegmentFilterValueSlotEmpty(filter.values[1])
        ) {
          filterError.values = {
            status: true,
            message: 'Please set the values properly.'
          }
        }
      } else if (valueCount === -1) {
        // For multi-value operators (like "in", "notIn"), at least one value is required
        if (!filter.values || filter.values.length < 1) {
          filterError.values = {
            status: true,
            message: 'Please set the values properly.'
          }
        }
      }
    }

    // Recursively validate children
    if (filter.children && filter.children.length > 0) {
      filterError.children = filter.children.map(child => validateFilter(child))
    }

    return filterError
  }

  // Validate form and return error count
  // skipName: when true, only validates filters (e.g. for count/preview - they don't need segment name)
  const validateForm = (opts?: { skipName?: boolean }): number => {
    let errorCount = 0
    const newErrorState: DynamicSegmentFormErrorStateType = {
      name: { status: false, message: '' },
      filters: []
    }

    // Rule 1 & 2: Validate name (skip when counting/previewing - API only needs filters)
    if (!opts?.skipName && (!segmentFormState.name || segmentFormState.name.trim() === '')) {
      newErrorState.name = {
        status: true,
        message: 'Segment name is required'
      }
      errorCount++
      setNameErrorKey(k => k + 1)
    }

    // Rule 3: Validate filters
    newErrorState.filters = segmentFormState.filters.map(filter => {
      const filterError = validateFilter(filter)
      
      // Count errors in this filter
      if (filterError.values.status) errorCount++
      
      // Count errors in children recursively
      const countChildErrors = (childErrors: DynamicSegmentFilterErrorType[]): void => {
        childErrors.forEach(childError => {
          if (childError.values.status) errorCount++
          if (childError.children.length > 0) {
            countChildErrors(childError.children)
          }
        })
      }
      
      if (filterError.children.length > 0) {
        countChildErrors(filterError.children)
      }
      
      return filterError
    })

    // Set the error state
    setFormErrorState(newErrorState)
    
    return errorCount
  }

  // Save segment function — returns error count when validation fails
  const saveSegment = async (): Promise<number | void> => {
    if (!merchantAccountId || !relationData.relationId) {
      return
    }
    const errorCount = validateForm()
    if (errorCount > 0) {
      return errorCount
    }

    const segmentIdToSave = typeof segmentId === 'string' ? segmentId : undefined
    const isEdit = isEditMode && Boolean(segmentIdToSave)

    setIsSavingSegment(true)
    try {
      const payload = createDynamicSegmentPayload(segmentFormState, relationData.relationId)
      
      const response: any = isEdit
        ? await editSegment('DYNAMIC', merchantAccountId, segmentIdToSave!, payload)
        : await createSegment('DYNAMIC', merchantAccountId, payload)

      if (response?.error) {
        errorToast(response.error.message || 'Something went wrong')
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
        return
      }

      successToast(isEdit ? 'Segment updated successfully' : 'Segment created successfully')

      setTempPreviewFilters(segmentFormState.previewFilters)
      setTempFilters(segmentFormState.filters)
      setTempName(segmentFormState.name)
      setTempDescription(segmentFormState.description ?? '')
      setTempTags(segmentFormState.tags ? [...segmentFormState.tags] : [])

      if (onSuccess) {
        onSuccess()
      } else {
        void router.back()
      }
    } catch (error) {
      console.error('Error saving segment:', error)
      errorToast((error as any)?.response?.data?.error?.message || 'Failed to save segment')
    } finally {
      setIsSavingSegment(false)
    }
  }

  const completeHydration = useCallback(() => {
    setIsLoadingSegment(false)
  }, [])

  // Before paint: avoid empty "Title" placeholder / untitled header flash while segment JSON is loading.
  useLayoutEffect(() => {
    if (!isEditMode || typeof segmentId !== 'string' || !segmentId) {
      if (!hydrateOnMount) {
        setIsLoadingSegment(false)
      }
      return
    }
    if (!merchantAccountId) {
      setIsLoadingSegment(true)
      return
    }
    const loadKey = `${merchantAccountId}:${segmentId}`
    if (segmentHydratedKeyRef.current === loadKey) {
      setIsLoadingSegment(false)
      return
    }
    setIsLoadingSegment(true)
  }, [isEditMode, segmentId, merchantAccountId, hydrateOnMount])

  // Fetch segment data in edit or read-only view mode
  useEffect(() => {
    let cancelled = false

    const fetchSegmentData = async () => {
      if (!isEditMode) {
        segmentHydratedKeyRef.current = null
        return
      }
      if (!segmentId || typeof segmentId !== 'string') return
      if (!merchantAccountId) return
      if (isLoadingRelationData || isLoadingSchemaData) return // Wait for schema to load

      const schema = schemaDataRef.current
      if (schema.fields.length === 0) return // Schema not ready yet

      const loadKey = `${merchantAccountId}:${segmentId}`
      if (segmentHydratedKeyRef.current === loadKey) {
        setIsLoadingSegment(false)
        return
      }

      setIsLoadingSegment(true)
      try {
        const response: any = await getSegmentById(merchantAccountId, segmentId)

        if (cancelled) return

        if (response?.error) {
          errorToast(response.error.message || 'Failed to load segment')
          console.error('x-trace-id:', response.headers?.['x-trace-id'])
          router.push('/segments')
          return
        }

        if (response?.data?.data) {
          const parsedFormState = parseSegmentResponseToFormState(response.data.data, schemaDataRef.current)
          setSegmentFormStateAction(parsedFormState)
          setTempPreviewFilters(parsedFormState.previewFilters)
          setTempFilters(parsedFormState.filters)
          setTempName(parsedFormState.name)
          setTempDescription(parsedFormState.description ?? '')
          setTempTags(parsedFormState.tags ? [...parsedFormState.tags] : [])
          segmentHydratedKeyRef.current = loadKey
        }
      } catch (error) {
        console.error('Error fetching segment:', error)
        if (!cancelled) {
          errorToast('Failed to load segment')
          router.push('/segments')
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSegment(false)
        }
      }
    }

    void fetchSegmentData()
    return () => {
      cancelled = true
    }
  }, [isEditMode, segmentId, merchantAccountId, isLoadingRelationData, isLoadingSchemaData, router, segmentReloadNonce])

  // Fetch template data in create mode when templateId is present (full-page /create?templateId=)
  useEffect(() => {
    if (!isCreateFromTemplate || !templateId || typeof templateId !== 'string') return
    let cancelled = false
    ;(async () => {
      const result = await fetchTemplateByIdAndApply(templateId)
      if (cancelled) return
      if (result === 'failed') {
        router.push('/segments')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isCreateFromTemplate, templateId, fetchTemplateByIdAndApply, router])

  // Dialog flow: apply template when parent sets templateIdToApply (schema may load after view switch)
  useEffect(() => {
    if (!templateIdToApply) return
    let cancelled = false
    ;(async () => {
      const result = await fetchTemplateByIdAndApply(templateIdToApply)
      if (cancelled) return
      if (result === 'success' || result === 'failed') {
        onTemplateApplied?.()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [templateIdToApply, fetchTemplateByIdAndApply, onTemplateApplied])

  const reloadSegmentFromApi = useCallback(() => {
    segmentHydratedKeyRef.current = null
    setIsLoadingSegment(true)
    setSegmentReloadNonce(n => n + 1)
  }, [])

  const discardWorkspace = useCallback(() => {
    if (onDiscard) {
      onDiscard()
      return
    }
    if (isViewMode) {
      void router.push('/segments')
      return
    }
    void router.back()
  }, [onDiscard, isViewMode, router])

  const isLoading = isLoadingSegment || merchantAccountId === ''

  const segmentContextValue: DynamicSegmentContextType = useMemo(
    () => ({
      segmentFormState,
      relationData,
      schemaData,
      hasPreviewChanges,
      formErrorState,
      nameErrorKey,
      isLoading,
      isLoadingSegment,
      isSavingSegment,
      isLoadingSchemaData,
      isEditMode,
      isViewMode,
      actions: {
        setSegmentFormState: setSegmentFormStateAction,
        setFormErrorState,
        addNewFilter,
        deleteFilter,
        updateFilter,
        setSegmentName,
        setSegmentDescription,
        setSegmentTags,
        setPreviewFilters,
        resetPreviewChanges,
        validateForm,
        saveSegment,
        applyTemplateById,
        reloadSegmentFromApi,
        discardWorkspace,
        completeHydration
      }
    }),
    [
      segmentFormState,
      relationData,
      schemaData,
      hasPreviewChanges,
      formErrorState,
      nameErrorKey,
      isLoading,
      isLoadingSegment,
      isSavingSegment,
      isLoadingSchemaData,
      isEditMode,
      isViewMode,
      setSegmentFormStateAction,
      setFormErrorState,
      addNewFilter,
      deleteFilter,
      updateFilter,
      setSegmentName,
      setSegmentDescription,
      setSegmentTags,
      setPreviewFilters,
      resetPreviewChanges,
      validateForm,
      saveSegment,
      applyTemplateById,
      reloadSegmentFromApi,
      discardWorkspace,
      completeHydration
    ]
  )

  return <DynamicSegmentContext.Provider value={segmentContextValue}>{children}</DynamicSegmentContext.Provider>
}

const useDynamicSegment = () => {
  const context = React.useContext(DynamicSegmentContext)
  if (context === undefined) {
    throw new Error('useDynamicSegment must be used within a DynamicSegmentProvider')
  }
  return context
}

export { DynamicSegmentProvider, useDynamicSegment }
